import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import postApi from '../../api/postApi';
import { useSearchParams } from 'react-router-dom';
import { mentionPrefix } from './comment-mention';

// Toàn bộ state + logic fetch/gửi/xoá bình luận của Q&A, tách khỏi Q&A.js
// (giữ nguyên hành vi gốc) để component chính chỉ còn phần render.
function useComments(postId) {
    const [comments, setComments] = useState([]);
    const [commentLimit, setCommentLimit] = useState(10);
    const [newComment, setNewComment] = useState('');
    const [newReply, setReply] = useState('');

    // Deep-link tới 1 bình luận/trả lời: /post/:id?comment=:commentId[&reply=:replyId]
    const [searchParams] = useSearchParams();
    const targetCommentId = searchParams.get('comment');
    const targetReplyId = searchParams.get('reply');
    const [highlightId, setHighlightId] = useState(null);
    const [highlightReplyId, setHighlightReplyId] = useState(null);
    const scrolledForRef = useRef(null); // key đã cuộn tới (tránh cuộn lại)

    const fetchComment = async (postId, limit) => {
        try {
            const { data } = await postApi.getAllComment(postId || 'shopee', limit);
            const rawComments = data?.post?.[0]?.comments ?? [];

            const normalizeUser = (u) => ({
                name: u?.name || 'user-unknown',
                avatar: u?.avatar || null,
                role: u?.role || 'user',
                rank: Number.isFinite(u?.rank) ? u.rank : 0,
                userId: u?.userId ?? 'unknown',
            });

            const normalized = rawComments.map((c) => ({
                ...c,
                user: normalizeUser(c?.user),
                replies: (c?.replies ?? []).map((r) => ({
                    ...r,
                    user: normalizeUser(r?.user),
                })),
                isOpenReply: false,
            }));

            setComments(normalized);
        } catch (error) {
            console.log(error);
        }
    };
    useEffect(() => {
        fetchComment(postId, commentLimit);
    }, [postId, commentLimit]);

    // Cuộn tới + đánh dấu bình luận/trả lời từ deep-link (?comment=&reply=).
    // Comment gốc (?comment=) dùng để đảm bảo đã tải đúng trang; nếu có ?reply=
    // thì cuộn tới đúng dòng reply (reply nằm trong comment gốc).
    useEffect(() => {
        if (!targetCommentId || comments.length === 0) return;
        const scrollKey = targetReplyId
            ? `reply-${targetReplyId}`
            : `comment-${targetCommentId}`;
        if (scrolledForRef.current === scrollKey) return;

        const found = comments.some(
            (c) => String(c.commentId) === String(targetCommentId)
        );
        if (!found) {
            if (comments.length >= commentLimit) {
                setCommentLimit((prev) => prev + 10); // còn dữ liệu → tải thêm
            }
            return;
        }

        scrolledForRef.current = scrollKey;
        const el = document.getElementById(scrollKey);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (targetReplyId) {
                setHighlightReplyId(String(targetReplyId));
                const t = setTimeout(() => setHighlightReplyId(null), 2500);
                return () => clearTimeout(t);
            }
            setHighlightId(String(targetCommentId));
            const t = setTimeout(() => setHighlightId(null), 2500);
            return () => clearTimeout(t);
        }
    }, [comments, targetCommentId, targetReplyId, commentLimit]);

    // mentionName: bấm "Trả lời" một REPLY thì prefill "@Tên " vào ô nhập
    // (kiểu Facebook) để người đọc biết reply đang nhắm tới ai.
    const onOpenReply = (commentId, mentionName) => {
        setReply(mentionPrefix(mentionName));
        setComments((prev) =>
            prev.map((el) => ({
                ...el,
                isOpenReply: el.commentId === commentId,
            }))
        );
    };

    // Bình luận của user thường vào hàng chờ duyệt (pending=true) — báo để user
    // không tưởng bị mất bình luận. Tài khoản bị khóa nhận message 403 từ server.
    const [pendingNotice, setPendingNotice] = useState(false);
    const pendingNoticeTimeoutRef = useRef(null);
    const notifyCommentResult = (data) => {
        if (data?.pending) {
            setPendingNotice(true);
            if (pendingNoticeTimeoutRef.current) {
                clearTimeout(pendingNoticeTimeoutRef.current);
            }
            pendingNoticeTimeoutRef.current = setTimeout(
                () => setPendingNotice(false),
                4000
            );
        }
    };
    const notifyCommentError = (error) => {
        toast.error(
            error?.response?.data?.message || 'Không thể gửi bình luận, vui lòng thử lại.'
        );
    };

    const onPostComment = async () => {
        if (!newComment) return;
        try {
            const { data } = await postApi.newComment(postId, newComment);
            notifyCommentResult(data);
            await fetchComment(postId);
            setNewComment('');
        } catch (error) {
            notifyCommentError(error);
        }
    };

    const onNewReply = async (commentId) => {
        if (!newReply) return;
        try {
            const { data } = await postApi.newReply(commentId, newReply);
            notifyCommentResult(data);
            await fetchComment(postId);
        } catch (error) {
            notifyCommentError(error);
        }
    };

    // Xoá là vĩnh viễn (không khôi phục được) → hỏi xác nhận, tránh bấm nhầm
    // vào nút nhỏ nằm sát nút "Trả lời".
    const onDeleteComment = async (commentId) => {
        if (!window.confirm('Xoá bình luận này? Bình luận và các trả lời bên trong sẽ mất vĩnh viễn.'))
            return;
        try {
            await postApi.deteleComment(commentId);
            await fetchComment(postId);
        } catch (error) {
            console.log(error);
        }
    };

    const onDeleteReply = async (replyId) => {
        if (!window.confirm('Xoá trả lời này? Không khôi phục được.')) return;
        try {
            await postApi.deteleReply(replyId);
            await fetchComment(postId);
        } catch (error) {
            console.log(error);
        }
    };

    const onLoadComment = () => {
        setCommentLimit((prev) => (prev += 10));
    };

    // Còn bình luận để tải tiếp? Server trả đủ `commentLimit` cái nghĩa là có
    // thể còn nữa; trả ít hơn là đã hết → Q&A ẩn nút "Tải thêm bình luận".
    const hasMoreComments = comments.length >= commentLimit;

    return {
        comments,
        hasMoreComments,
        newComment,
        setNewComment,
        newReply,
        setReply,
        highlightId,
        highlightReplyId,
        pendingNotice,
        onOpenReply,
        onPostComment,
        onNewReply,
        onDeleteComment,
        onDeleteReply,
        onLoadComment,
    };
}

export default useComments;
